import React from 'react';
import styled from 'styled-components';
import UnifiedModal from './UnifiedModal';
import { useTranslation } from 'react-i18next';

const Content = styled.div`
  padding: 1rem 0;
  color: #374151;
  font-size: 1rem;
  line-height: 1.5;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 0.75rem;
  width: 100%;
  justify-content: flex-end;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;

  ${props => props.$variant === 'primary' && `
    background: var(--primary-orange, #ea580c);
    color: white;
    &:hover { background: #d9480a; }
  `}

  ${props => props.$variant === 'secondary' && `
    background: white;
    color: #374151;
    border-color: #d1d5db;
    &:hover { background: #f9fafb; border-color: #9ca3af; }
  `}

  ${props => props.$variant === 'danger' && `
    background: #ef4444;
    color: white;
    &:hover { background: #dc2626; }
  `}
`;

interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'primary' | 'danger';
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  variant = 'primary'
}) => {
  const { t } = useTranslation();

  const footer = (
    <FooterActions>
      <Button $variant="secondary" onClick={onClose}>
        {cancelText || t('portal.common.cancel', 'Cancel')}
      </Button>
      <Button 
        $variant={variant === 'danger' ? 'danger' : 'primary'} 
        onClick={() => {
          onConfirm();
          onClose();
        }}
      >
        {confirmText || t('portal.common.confirm', 'Confirm')}
      </Button>
    </FooterActions>
  );

  return (
    <UnifiedModal
      open={open}
      onClose={onClose}
      title={title}
      variant="center"
      size="sm"
      footer={footer}
    >
      <Content>
        {message}
      </Content>
    </UnifiedModal>
  );
};

export default ConfirmModal;
